import React, { useEffect, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { AppButton, Callout, IconButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import QuestionForm from "../CodingQuestionBank/QuestionForm";
import { adminGetQuickQuiz, adminUpdateQuickQuiz, updateSessionAssignment } from "../../utils/smartLearning";
import { resourceRowMeta } from "./resourceKinds";
import QuizForm from "./QuizForm";
import AssignmentForm from "./AssignmentForm";

const toDateInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
};
const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";

/**
 * ResourceEditor — edit an attached quiz, assignment or coding question in
 * place, with the same forms used to create one. A sectioned / advanced quiz
 * cannot be edited here and offers its full editor instead.
 *
 * Props:
 *   resource     — { kind, refId, title }
 *   courseId, context ({ moduleTitle, sessionTitle, sessionDescription })
 *   onSaved(saved), onClose
 *   onOpenFull(kind, refId) — leave for the full editor (the caller confirms)
 */
const ResourceEditor = ({ resource, courseId, context = {}, onSaved, onClose, onOpenFull }) => {
  const { kind, refId } = resource || {};
  const { Icon, label } = resourceRowMeta(resource);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!refId || !kind) return undefined;
    let alive = true;
    setLoading(true);
    setError("");
    const load = kind === "quiz"
      ? adminGetQuickQuiz(refId)
      : kind === "codingChallenge"
        ? apiClient.get(`/questions/${refId}`).then((r) => r.data?.question || null)
        : kind === "assignment"
          ? apiClient.get(`/general-assignments/detail/${refId}`).then((r) => r.data?.assignment || null)
          : Promise.resolve(null);
    Promise.resolve(load)
      .then((res) => { if (alive) setData(res); })
      .catch((err) => { if (alive) setError(err?.response?.data?.message || "This resource could not be loaded."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, refId]);

  const done = (saved) => {
    onSaved?.(saved);
    onClose();
  };

  const saveCoding = async (q) => {
    setSaveError("");
    try {
      const { data: r } = await apiClient.put(`/questions/${q._id}`, q);
      done(r?.question || q);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "The question was not saved. Try again.");
    }
  };

  let body = null;
  if (loading) body = <Shimmer height={56} count={3} />;
  else if (error) body = <Callout tone="danger" role="alert">{error}</Callout>;
  else if (kind === "codingChallenge" && data) {
    body = (
      <div className="tw-flex tw-flex-col tw-gap-3">
        {saveError && <Callout tone="danger" role="alert">{saveError}</Callout>}
        <QuestionForm question={data} onSave={saveCoding} onCancel={onClose} />
      </div>
    );
  } else if (kind === "quiz" && data) {
    body = data.editable ? (
      <QuizForm
        courseId={courseId}
        context={context}
        initialQuiz={data.quiz}
        submitFn={(payload) => adminUpdateQuickQuiz(refId, payload)}
        submitLabel="Save changes"
        onCreated={done}
        onCancel={onClose}
      />
    ) : (
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">{data.summary?.title || "Quiz"}</p>
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">
          {data.summary?.questionCount || 0} questions{data.summary?.mode ? ` · ${String(data.summary.mode).replace("_", " ")}` : ""}.
          It has sections or advanced settings, so it is edited in the full assessment editor.
        </p>
        <AppButton onClick={() => onOpenFull?.("quiz", refId)}>
          <ExternalLink size={14} aria-hidden="true" /> Open the full editor
        </AppButton>
      </div>
    );
  } else if (kind === "assignment" && data) {
    body = (
      <AssignmentForm
        courseId={courseId}
        initial={{
          batchId: String(idOf(data.batchId) || idOf(data.batch)),
          title: data.title || "",
          dueDate: toDateInput(data.dueDate),
          maxMarks: data.maxMarks ?? 10,
          description: data.description || "",
        }}
        submitFn={(fd) => updateSessionAssignment(refId, fd)}
        submitLabel="Save changes"
        onCreated={done}
        onCancel={onClose}
      />
    );
  } else {
    body = <p className="tw-m-0 tw-text-sm tw-text-content-muted">This kind of resource has nothing to edit here.</p>;
  }

  return (
    <section aria-label={`Edit ${label.toLowerCase()}`} className="tw-flex tw-min-h-0 tw-flex-col tw-gap-4">
      <header className="tw-flex tw-items-center tw-gap-2.5">
        <span className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
          <Icon size={16} aria-hidden="true" />
        </span>
        <h3 className="tw-m-0 tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.9375rem] tw-font-semibold tw-text-content">
          Edit {resource?.title || label.toLowerCase()}
        </h3>
        <IconButton size="sm" icon={X} label="Close" onClick={onClose} />
      </header>
      {body}
    </section>
  );
};

export default ResourceEditor;
